/* Purpose: presentation bridges for the pilot's native purchase controls.
 * Usage: loaded by the dedicated main section; delegation covers section reloads.
 * Scope: pilot root only. No requests or add-to-cart ownership. Prices stay rendered
 * by Liquid/native variants and the existing compact-card script.
 */
(() => {
  if (window.mvkPdpLayoutReady) return;
  window.mvkPdpLayoutReady = true;
  let stickyObserver;
  let paymentTermsObserver;
  // Shopify renders the installment sentence inside an open shadow root with
  // its own GTStandard font. Keep its amount, logo, disclosure and modal native;
  // bridge only that sentence to the PDP's shared caption type role.
  const syncPaymentTermsType = () => {
    const root = document.querySelector('[data-mvk-pdp-main]');
    root?.querySelectorAll('.shopify-payment-terms shopify-payment-terms').forEach(host => {
      const shadow = host.shadowRoot;
      if (!shadow || shadow.querySelector('style[data-mvk-payment-type]')) return;
      const style = document.createElement('style');
      style.dataset.mvkPaymentType = '';
      style.textContent = `
        #shopify-installments-content {
          font-family: var(--mvk-font-body);
          font-size: var(--mvk-size-caption);
          font-weight: var(--mvk-weight-regular);
          line-height: var(--mvk-leading-body);
          letter-spacing: var(--mvk-tracking-body);
          color: var(--mvk-color-secondary);
        }
        #shopify-installments-content .font-bold {
          font-weight: var(--mvk-weight-medium);
        }
      `;
      shadow.appendChild(style);
    });
  };
  const updateSticky = () => {
    const root = document.querySelector('[data-mvk-pdp-main]');
    if (!root) return;
    // Keep the mobile purchase controls available from page load. The in-flow
    // form remains the native variant and cart owner for both buttons.
    const mobile = window.matchMedia('(max-width: 768px)').matches;
    const visible = root.dataset.mvkStickyEnabled !== 'false'
      && mobile;
    root.dataset.mvkStickyVisible = visible ? 'true' : 'false';
    const bar = root.querySelector('.float--add-to-cart--wrapper');
    if (bar) {
      // This wrapper is the ordinary in-flow desktop CTA as well as the
      // mobile fixed CTA. Only conceal it from interaction when disabled on
      // mobile; desktop must remain clickable.
      bar.inert = mobile && !visible;
      bar.setAttribute('aria-hidden', mobile && !visible ? 'true' : 'false');
    }
  };
  const syncPreorder = (root, variant) => {
    const source = root?.querySelector('[data-mvk-preorder-dates]');
    if (!source) return;
    let dates;
    try { dates = JSON.parse(source.textContent); } catch (_) { return; }
    const id = variant === undefined ? root.querySelector('[data-product-select]')?.value : variant?.id;
    const date = dates[String(id)] || '';
    const preorder = Boolean(date) || source.dataset.mvkForcePreorder === 'true';
    const regularText = root.querySelector('[data-mvk-cart-text]')?.dataset.mvkCartText;
    root.querySelectorAll('[data-add-to-cart-text]').forEach(label => {
      label.dataset.defaultText = preorder ? label.dataset.preorderText : regularText;
      // The native controller owns disabled/sold-out state. Its document event
      // fires after its own button update, so restore the selected CTA wording.
      if ((variant === undefined || variant?.available) && !label.closest('button')?.disabled) {
        label.textContent = label.dataset.defaultText;
      }
    });
    root.querySelectorAll('.preorder-date-input').forEach(input => {
      input.value = date;
      if (date) input.setAttribute('value', date);
      else input.removeAttribute('value');
      input.disabled = !date;
    });
    const note = root.querySelector('[data-mvk-preorder-info]');
    if (note) {
      note.hidden = !date;
      note.textContent = date ? `${note.dataset.mvkPreorderPrefix} ${date}` : '';
    }
  };
  const initialize = () => {
    stickyObserver?.disconnect();
    paymentTermsObserver?.disconnect();
    const root = document.querySelector('[data-mvk-pdp-main]');
    const bar = root?.querySelector('.float--add-to-cart--wrapper');
    if (!bar) return;
    stickyObserver = new ResizeObserver(() => document.documentElement.style.setProperty('--mvk-pdp-sticky-height', `${Math.ceil(bar.getBoundingClientRect().height)}px`));
    stickyObserver.observe(bar);
    syncPaymentTermsType();
    // The component upgrades asynchronously and may re-render after a variant
    // change. Observe only its native wrapper, never the full PDP subtree.
    paymentTermsObserver = new MutationObserver(syncPaymentTermsType);
    root.querySelectorAll('.shopify-payment-terms').forEach(wrapper => {
      paymentTermsObserver.observe(wrapper, {childList: true, subtree: true});
    });
    customElements.whenDefined('shopify-payment-terms').then(syncPaymentTermsType);
    syncPreorder(root);
    updateSticky();
  };
  document.addEventListener('DOMContentLoaded', initialize, {once: true});
  window.addEventListener('load', initialize, {once: true});
  document.addEventListener('shopify:section:load', initialize);
  window.addEventListener('resize', updateSticky, {passive: true});
  if (document.readyState !== 'loading') initialize();
  // Capture form submission before the theme's AJAX cart handler reads the
  // line-item properties; third-party scripts may reset hidden fields later.
  document.addEventListener('submit', event => {
    const root = event.target.closest?.('[data-mvk-pdp-main]');
    if (root) syncPreorder(root);
  }, true);
  document.addEventListener('change', event => {
    const menu = event.target.closest?.('[data-mvk-sticky-variant]');
    if (menu) {
      const root = menu.closest('[data-mvk-pdp-main]');
      const values = JSON.parse(menu.selectedOptions[0].dataset.options);
      const inputs = [...root.querySelectorAll('.variant-wrapper [data-variant-input]')];
      let last;
      // Set every option before emitting one native change: avoid intermediate
      // invalid combinations for products with more than one option.
      values.forEach((value, index) => {
        const input = inputs.find(e => e.dataset.index === `option${index + 1}` && (e.tagName === 'SELECT' || e.value === value));
        if (input) {
          if (input.tagName === 'SELECT') input.value = value;
          else input.checked = true;
          last = input;
        }
      });
      if (last) last.dispatchEvent(new Event('change', {bubbles: true}));
    }
    const pair = event.target.closest?.('.mvk-pdp-pair select');
    if (pair) {
      const card = pair.closest('.mvk-pdp-pair');
      card.querySelectorAll('[data-mvk-pair-link]').forEach(a => { a.href = pair.selectedOptions[0].dataset.url; });
      card.dataset.variantId = pair.value;
    }
  });
  document.addEventListener('variant:change', event => {
    const root = document.querySelector('[data-mvk-pdp-main]');
    const variant = event.detail?.variant;
    const source = root?.querySelector('[data-mvk-preorder-dates]');
    if (!variant || !source) return;
    // The theme also emits this document event for quick views. Match IDs to
    // this main product before changing its purchase and location controls.
    let dates;
    try { dates = JSON.parse(source.textContent); } catch (_) { return; }
    if (!Object.prototype.hasOwnProperty.call(dates, String(variant.id))) return;
    const menu = root.querySelector('[data-mvk-sticky-variant]');
    if (menu) menu.value = String(variant.id);
    syncPreorder(root, variant);
    requestAnimationFrame(() => syncPreorder(root, variant));
    // The location estimator owns warehouse/date calculations. Keep its
    // selected variant ID current and ask it to recalculate after native UI.
    const country = root.querySelector('.estimated-country-select');
    if (country) {
      country.dataset.vid = String(variant.id);
      requestAnimationFrame(() => {
        if (getComputedStyle(country.closest('.estimate-shipping-block')).display !== 'none') {
          window.dispatchEvent(new CustomEvent('fetchProductEstimatedDate', {detail: {country: country.value}}));
        }
      });
    }
  }, true);
})();
